/**
 * CheckoutController owns the lifecycle of a single embedded checkout for one
 * payment session. Shared by the inline custom element and the modal.
 *
 * The merchant-facing UI is the same hosted page that lives at
 * `${checkoutUrl}/${sessionId}`. We just iframe it — that way every change to
 * the page (translations, wallet ordering, branding, accessibility) carries
 * over without a separate code path here.
 *
 * The hosted page reads the merchant's client_secret from the URL hash
 * (`#cs=pcs_…`), which never leaves the browser, and posts a message back to
 * us when the payment settles. We translate that into onComplete / onError.
 */

import type { CheckoutOptions } from "./types";
import { DEFAULT_CHECKOUT_URL } from "./api";

const ALLOWED_MESSAGE_TYPES = new Set([
  "borga:complete",
  "borga:error",
  "borga:cancel",
  // Progress messages. Only sent by newer checkout deployments — everything
  // that consumes them has a fallback, see CheckoutControllerOptions.
  "borga:ready",
  "borga:resize",
]);

interface BorgaMessage {
  type: string;
  paymentId?: string;
  message?: string;
  code?: string;
  /** `borga:resize` payload. Untrusted — validated before use. */
  height?: unknown;
}

/**
 * Internal options. Not part of the public SDK surface: `CheckoutOptions` is
 * what merchants pass to `borga.checkout.open()`, these extras are how the
 * modal shell listens in on the hosted page's progress messages.
 */
export interface CheckoutControllerOptions extends CheckoutOptions {
  /**
   * The hosted page has loaded the session and is painting real content.
   * Never guaranteed — an older checkout deployment doesn't send it — so
   * callers must have a time/`onFrameLoad`-based fallback.
   */
  onReady?: () => void;
  /**
   * The hosted page reported its content height in CSS pixels. Validated to
   * be a finite positive number before it reaches the caller. Also optional:
   * with no messages the caller must keep its default sizing.
   */
  onResize?: (height: number) => void;
  /** The iframe fired its first `load` event. */
  onFrameLoad?: () => void;
}

/**
 * Owns the cross-origin checkout iframe: builds its URL (session id in the
 * path, client secret in the hash so it never reaches a server), mounts it,
 * and translates the hosted page's `postMessage` traffic into the
 * `CheckoutControllerOptions` callbacks. Messages are accepted only from the
 * checkout origin.
 */
export class CheckoutController {
  private iframe: HTMLIFrameElement | null = null;
  private destroyed = false;
  private settled = false;
  private readonly options: CheckoutControllerOptions;
  private readonly checkoutUrl: string;
  private readonly checkoutOrigin: string;
  private readonly onMessage: (e: MessageEvent) => void;

  constructor(options: CheckoutControllerOptions) {
    this.options = options;
    this.checkoutUrl = options.checkoutUrl || DEFAULT_CHECKOUT_URL;
    this.checkoutOrigin = new URL(this.checkoutUrl).origin;
    this.onMessage = (e) => this.handleMessage(e);
  }

  /**
   * Mount the checkout iframe into the given host element.
   */
  async mount(host: HTMLElement): Promise<void> {
    if (this.destroyed) return;

    const iframe = document.createElement("iframe");
    iframe.src = this.buildIframeUrl();
    iframe.title = "Payment";
    // shared-autofill: allow card autofill in the cross-origin frame.
    iframe.allow = "payment *; publickey-credentials-get *; shared-autofill *";
    iframe.style.width = "100%";
    iframe.style.minHeight = "560px";
    iframe.style.border = "0";
    iframe.style.display = "block";
    iframe.style.background = "#fff";

    // First load only — the iframe navigates again during a 3DS redirect and
    // the shell has long since revealed it by then.
    iframe.addEventListener(
      "load",
      () => {
        if (!this.destroyed) this.options.onFrameLoad?.();
      },
      { once: true },
    );

    host.appendChild(iframe);
    this.iframe = iframe;

    window.addEventListener("message", this.onMessage);
  }

  /**
   * Remove the iframe and stop listening for messages. Fires no callback;
   * safe to call more than once.
   */
  destroy() {
    this.destroyed = true;
    window.removeEventListener("message", this.onMessage);
    if (this.iframe?.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
    }
    this.iframe = null;
  }

  /** Tear down and report the checkout as canceled by the payer. */
  cancel() {
    this.destroy();
    this.options.onCancel?.();
  }

  private buildIframeUrl(): string {
    const base = `${this.checkoutUrl.replace(/\/$/, "")}/${encodeURIComponent(
      this.options.sessionId,
    )}`;
    // Hash only — never sent to any server, available to the iframed page
    // via window.location.hash.
    const hash = `#cs=${encodeURIComponent(this.options.clientSecret)}`;
    return `${base}${hash}`;
  }

  private handleMessage(event: MessageEvent) {
    if (event.origin !== this.checkoutOrigin) return;
    if (event.source !== this.iframe?.contentWindow) return;
    const data = event.data as BorgaMessage | undefined;
    if (!data || typeof data !== "object") return;
    if (!ALLOWED_MESSAGE_TYPES.has(data.type)) return;

    // Progress messages are advisory and repeatable — they must not trip the
    // settle latch, which exists so the first terminal outcome wins.
    if (data.type === "borga:ready") {
      this.options.onReady?.();
      return;
    }
    if (data.type === "borga:resize") {
      const height = data.height;
      if (typeof height !== "number") return;
      if (!Number.isFinite(height) || height <= 0) return;
      this.options.onResize?.(height);
      return;
    }

    if (this.settled) return;
    this.settled = true;

    if (data.type === "borga:complete") {
      this.options.onComplete?.({
        paymentId: data.paymentId || this.options.sessionId,
        status: "succeeded",
      });
    } else if (data.type === "borga:error") {
      this.options.onError?.({
        message: data.message || "Payment failed.",
        code: data.code,
      });
    } else if (data.type === "borga:cancel") {
      this.options.onCancel?.();
    }
  }
}
